import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUploader } from "@/components/admin/ImageUploader";
import { MapPinField } from "@/components/admin/MapPinField";
import { BrochureField } from "@/components/admin/BrochureField";

export const SCHEMAS = {
  properties: {
    label: "property",
    empty: { title: "", listing_type: "For Sale", property_type: "Residential", location: "", price: "", bedrooms: "", bathrooms: "",
      area: "", description: "", features: "", images: [], status: "Available", featured: false, international: false, country: "", brochure: "", brochure_name: "", lat: null, lng: null },
    fields: [
      { k: "title", label: "Title", span: 2, required: true },
      { k: "listing_type", label: "Listing", options: ["For Sale", "For Lease", "Investment"] },
      { k: "property_type", label: "Type", options: ["Residential", "Commercial", "Land", "Industrial", "Mixed-use"] },
      { k: "price", label: "Price (as shown)", placeholder: "e.g. $450,000 or Price on request" },
      { k: "location", label: "Location" },
      { k: "bedrooms", label: "Bedrooms", type: "number" },
      { k: "bathrooms", label: "Bathrooms", type: "number" },
      { k: "area", label: "Size", placeholder: "e.g. 450 sqm" },
      { k: "status", label: "Status", options: ["Available", "Under Offer", "Sold", "Let"] },
      { k: "country", label: "Country (international listings)", placeholder: "e.g. United Arab Emirates" },
      { k: "description", label: "Description", span: 2, textarea: true },
      { k: "features", label: "Features (one per line)", span: 2, textarea: true, list: true },
    ],
  },
  projects: {
    label: "project",
    empty: { title: "", location: "", status: "Ongoing", category: "Residential", units: "", completion: "", description: "",
      highlights: "", images: [], featured: false, brochure: "", brochure_name: "", lat: null, lng: null },
    fields: [
      { k: "title", label: "Project name", span: 2, required: true },
      { k: "status", label: "Status", options: ["Completed", "Ongoing", "Upcoming"] },
      { k: "category", label: "Category", options: ["Residential", "Commercial", "Mixed-use", "Industrial", "Infrastructure"] },
      { k: "location", label: "Location" },
      { k: "units", label: "Units", placeholder: "e.g. 24 homes" },
      { k: "completion", label: "Completion", placeholder: "e.g. Q4 2026" },
      { k: "description", label: "Description", span: 2, textarea: true },
      { k: "highlights", label: "Highlights (one per line)", span: 2, textarea: true, list: true },
    ],
  },
  shortlets: {
    label: "shortlet",
    empty: { title: "", location: "", nightly_rate: "", cleaning_fee: "", min_nights: 1, max_guests: 2, bedrooms: "", bathrooms: "",
      description: "", amenities: "", house_rules: "", images: [], featured: false, lat: null, lng: null },
    fields: [
      { k: "title", label: "Apartment name", span: 2, required: true },
      { k: "location", label: "Location" },
      { k: "nightly_rate", label: "Nightly rate (₦)", type: "number", required: true, placeholder: "e.g. 85000" },
      { k: "cleaning_fee", label: "Cleaning fee per stay (₦)", type: "number", placeholder: "0" },
      { k: "min_nights", label: "Minimum nights", type: "number" },
      { k: "max_guests", label: "Maximum guests", type: "number" },
      { k: "bedrooms", label: "Bedrooms", type: "number" },
      { k: "bathrooms", label: "Bathrooms", type: "number" },
      { k: "description", label: "Description", span: 2, textarea: true },
      { k: "amenities", label: "Amenities (one per line)", span: 2, textarea: true, list: true },
      { k: "house_rules", label: "House rules (one per line)", span: 2, textarea: true, list: true },
    ],
  },
};

const numKeys = (schema) => schema.fields.filter((x) => x.type === "number").map((x) => x.k);
const DEFAULT_NUM = { min_nights: 1, max_guests: 2, cleaning_fee: 0, nightly_rate: 0 };

const toForm = (schema, item) => {
  if (!item) return { ...schema.empty };
  const f = { ...schema.empty, ...item };
  schema.fields.filter((x) => x.list).forEach((x) => { f[x.k] = (item[x.k] || []).join("\n"); });
  numKeys(schema).forEach((k) => { f[k] = f[k] ?? ""; });
  return f;
};

const toPayload = (schema, form) => {
  const p = {};
  Object.keys(schema.empty).forEach((k) => { p[k] = form[k]; });
  schema.fields.filter((x) => x.list).forEach((x) => { p[x.k] = form[x.k].split("\n").map((s) => s.trim()).filter(Boolean); });
  numKeys(schema).forEach((k) => { p[k] = p[k] === "" ? (DEFAULT_NUM[k] ?? null) : Number(p[k]); });
  return p;
};

const Field = ({ f, value, onChange }) => {
  const id = `field-${f.k}`;
  return (
    <div className={f.span === 2 ? "sm:col-span-2" : ""}>
      <Label htmlFor={id} className="lab text-gw-ground">{f.label}</Label>
      {f.options ? (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger id={id} className="mt-2 rounded-none" data-testid={`admin-form-${f.k}-select`}><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : f.textarea ? (
        <Textarea id={id} rows={4} value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 rounded-none" data-testid={`admin-form-${f.k}-input`} />
      ) : (
        <Input id={id} type={f.type || "text"} required={f.required} value={value} placeholder={f.placeholder}
          onChange={(e) => onChange(e.target.value)} className="mt-2 rounded-none" data-testid={`admin-form-${f.k}-input`} />
      )}
    </div>
  );
};

export const ItemFormDialog = ({ kind, item, open, onOpenChange, onSaved }) => {
  const schema = SCHEMAS[kind];
  const [form, setForm] = useState(toForm(schema, item));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(toForm(schema, item)); }, [open, item, schema]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = toPayload(schema, form);
      if (item) await http.put(`/${kind}/${item.id}`, payload);
      else await http.post(`/${kind}`, payload);
      toast.success(`${item ? "Updated" : "Published"} ${schema.label}`);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-gw-deep" data-testid="admin-item-dialog">
        <DialogHeader>
          <DialogTitle className="lux text-3xl font-normal">{item ? `Edit ${schema.label}` : `New ${schema.label}`}</DialogTitle>
          <DialogDescription>Published items appear on the public site immediately.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-5 mt-2">
          <div className="sm:col-span-2">
            <Label className="lab text-gw-ground">Photos (first is the cover)</Label>
            <div className="mt-2"><ImageUploader images={form.images} onChange={set("images")} /></div>
          </div>
          {schema.fields.map((f) => <Field key={f.k} f={f} value={form[f.k]} onChange={set(f.k)} />)}
          {kind !== "shortlets" && <BrochureField path={form.brochure} name={form.brochure_name} onChange={(v) => setForm((f) => ({ ...f, ...v }))} />}
          <MapPinField lat={form.lat} lng={form.lng} location={form.location}
            onChange={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))} />
          <div className="sm:col-span-2 flex items-center gap-3">
            <Switch id="featured" checked={form.featured} onCheckedChange={set("featured")} data-testid="admin-form-featured-switch" />
            <Label htmlFor="featured">Featured</Label>
            {kind === "properties" && (
              <>
                <Switch id="international" checked={!!form.international} onCheckedChange={set("international")} className="ml-6" data-testid="admin-form-international-switch" />
                <Label htmlFor="international">International listing (shows under the International tab)</Label>
              </>
            )}
          </div>
          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="lab px-5 py-3 border border-gw-ground/30 hover:bg-gw-tint transition-colors duration-200" data-testid="admin-form-cancel-button">Cancel</button>
            <button type="submit" disabled={busy} className="lab px-5 py-3 bg-gw-ground text-white hover:bg-gw-deep transition-colors duration-200 inline-flex items-center gap-2 disabled:opacity-60" data-testid="admin-form-save-button">
              {busy && <Loader2 size={14} className="animate-spin" />}{item ? "Save changes" : "Publish"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
